<template>«
    <div class="attach-gallery">
        <div class="header gallery-header">
            <h2 class="title">{{attachment.name || null}}</h2>

            <div class="actions">
                <LinkButton v-if="attachment.download || attachment.downloadUrl"
                    :link="attachment.download_url || attachment.contents || attachment.download"
                    :download="attachment.name"
                    v-tooltip='"messages.open.attachments.download"'
                    icon='fas fa-download'
                />
                <!-- <ActionButton @click="onUploadToCloud()"
                    v-tooltip='"messages.open.attachments.upload_cloud"'
                    icon='fas fa-cloud-upload-alt'
                /> -->
                <ActionButton
                    @click="onClose()"
                    v-tooltip='"messages.actions.close"'
                    class="close"
                    icon="fas fa-times-circle"
                />
            </div>
        </div>

        <div class="gallery-body">
            <div class="loader" v-if="attachment.fetching">
                <i class="fas fa-spinner fa-spin"></i>
            </div>

            <div v-else-if="supported_previews[attachment.type]" class="attachment">
                <div v-if="attachment.type === 'image'" class="attachment attachment-preview preview-image">
                    <img
                        :src="attachment.contents"
                        class="preview-image"
                    />
                </div>

                <div v-else-if="attachment.type === 'video'" class="attachment attachment-preview preview-video">
                    <video
                        :src="attachment.contents"
                        controls
                        class="preview-video"
                    />
                </div>

                <div v-else-if="attachment.type === 'audio'" class="attachment-preview preview-audio">
                    <audio
                        :src="attachment.contents"
                        controls
                    />
                </div>

                <div v-else-if="attachment.type === 'text'" class="attachment attachment-preview preview-text">
                    <pre>{{ attachment.contents }}</pre>
                </div>

                <div v-else-if="attachment.type === 'pdf'" class="attachment attachment-preview preview-pdf">
                    <template v-if="!LayoutSettingsController.is_mobile">
                        <object
                            :data="attachment.contents"
                            type="application/pdf"
                            class="preview-pdf"
                        >
                        </object>
                    </template>
                    <template v-else>
                        <div class="attachment" :class="'type-' + attachment.type">
                            <div class="actions">
                                <p>{{attachment.name}} <span>{{attachment.size}}</span></p>
                                <LinkButton
                                    :link="attachment.download"
                                    icon='fas fa-download'
                                    :text="'messages.open.attachments.download'"
                                />

                                <LinkButton
                                    :link="attachment.preview_url"
                                    target="_blank"
                                    icon='far fa-eye'
                                    :text="'messages.open.attachments.preview'"
                                />
                            </div>
                        </div>
                    </template>
                </div>
            </div>

            <div v-else class="attachment" :class="'type-' + attachment.type">
                <div class="actions">
                    <p>{{attachment.name}} <span>{{attachment.size}}</span></p>
                    <LinkButton
                        :link="attachment.download"
                        icon='fas fa-download'
                        :text="'messages.open.attachments.download'"
                    />
                </div>
            </div>
        </div>

        <div v-if="attachments.count > 1" class="gallery-navigation">
            <ActionButton v-show="active_index > 0"
                @click="onPrevious()"
                icon='fas fa-chevron-left'
                class='left'
              />
            <ActionButton v-show="active_index < attachments.count - 1"
                @click="onNext()"
                icon='fas fa-chevron-right'
                class='right'
            />
        </div>

        <!--<div class="gallery-footer">
            <ActionButton icon='fas fa-search-minus' class='tooltip top' label='Diminuir' />
            <ActionButton icon='fas fa-search-plus' class='tooltip top' label='Aumentar' />
        </div>-->
    </div>
</template>

<script setup>
    import {nextTick, ref}              from 'vue';
    import {defineProps, defineEmits}   from 'vue';
    import {onBeforeMount, onUnmounted} from 'vue';
    import ActionButton                 from '@/vue/Elements/Buttons/ActionButton.vue';
    import LinkButton                   from '@/vue/Elements/Buttons/LinkButton.vue';
    import KeyboardController           from '@/js/Webmail/Keyboard/KeyboardController';
    import { KeyboardProfile }          from "@/js/Webmail/Keyboard/KeyboardMetadata";
    import LayoutSettingsController     from '@/js/Webmail/LayoutSettingsController';
    import MessageAttachmentsItem       from '@/js/Mailboxes/Entities/Message/MessageAttachmentsItem';

    const emit = defineEmits([
        'galleryClose'
    ]);

    const props = defineProps({
        attachments : {
            required: true,
        },

        options : {
            required : true,
        }
    });

    const LOAD_METHODS       = {}
    const attachment         = ref(null);
    const active_index       = ref();
    // Local copy: loadPreview() below injects a synthetic entry keyed by
    // mime-part-id, which is not a valid position in props.attachments.items.
    // Mutating that shared array corrupts MessagesAttachments.vue's own v-for.
    const items               = ref([...props.attachments.items]);
    const supported_previews = {
        'image'  : true,
        'pdf'    : true,
        'video'  : true,
        'audio'  : true,
        'text'   : true,
    };

    onBeforeMount(() => {
        setLoadMethods();
        setActiveIndex(props.options.index);
        open();

        KeyboardController.disableAll();
        KeyboardController.create(KeyboardProfile.ATTACHMENTS_GALLERY, {
        close : onKeyboardClose,
        })
    })

    onUnmounted(() => {
        KeyboardController.delete(KeyboardProfile.ATTACHMENTS_GALLERY);
        KeyboardController.enableAll();
    })

    const onClose = () => {
        emit('galleryClose');
    }

    const onUploadToCloud = () => {
    }

    const onKeyboardClose = () => {
        onClose();
    }

    const onNext = () => {
        setActiveIndex(active_index.value + 1);
        open();
    }

    const onPrevious = () => {
        setActiveIndex(active_index.value - 1);
        open();
    }

    const open = () => {
        attachment.value = items.value[active_index.value];
        let type         = (attachment.value) ? attachment.value.type : 'preview';

        if (LOAD_METHODS[type]) {
            LOAD_METHODS[type]();
        }
    }

    const loadImage = async () => {
        await nextTick();
        fetchBlob(attachment.value.preview_url);
    }

    const loadVideo = async () => {
        await nextTick();
        fetchBlob(attachment.value.download);
    }

    const loadAudio = async () => {
        await nextTick();
        fetchBlob(attachment.value.download);
    }

    const loadText = async () => {
        await nextTick();
        fetchText(attachment.value.download);
    }

    const loadPdf = async () => {
        await nextTick();
        fetchBlob(attachment.value.preview_url);
    }

    /**
     * Hack
     *
     * Generate an attachment on the fly
     */
    const loadPreview = async () => {
        if (items.value[active_index.value]) {
            return;
        }

        attachment.value          = new MessageAttachmentsItem();
        attachment.value.name     = null;
        attachment.value.download = null;
        attachment.value.type     = 'image';

        items.value[active_index.value] = attachment.value;

        await nextTick();
        fetchBlob(props.options.preview);
    }

    const fetchBlob = async (url) => {
        const current_active_index = active_index.value;
        const attachment_item      = items.value[current_active_index];

        if (attachment_item.fetching || attachment_item.contents) {
            return;
        }


        attachment_item.fetching = true;
        const response           = await fetch(url);

        // Need a retry or something here
        if (!response.ok) {
            attachment_item.fetching = false;
            return;
        }

        attachment_item.contents = URL.createObjectURL(await response.blob());
        attachment_item.fetching = false;
    }

    const fetchText = async (url) => {
        const current_active_index = active_index.value;
        const attachment_item      = items.value[current_active_index];

        if (attachment_item.fetching || attachment_item.contents) {
            return;
        }

        attachment_item.fetching = true;
        const response           = await fetch(url);

        // Need a retry or something here
        if (!response.ok) {
            attachment_item.fetching = false;
            return;
        }

        // Read the body once as ArrayBuffer
        const arrayBuffer = await response.arrayBuffer();
        let text;

        try {
            text = new TextDecoder('utf-8').decode(arrayBuffer);
            // Heuristic: if text contains replacement chars, try Windows-1252
            if (/\ufffd/.test(text)) {
                text = new TextDecoder('windows-1252').decode(arrayBuffer);
            }
        } catch (Error) {
            text = new TextDecoder('windows-1252').decode(arrayBuffer);
        }
        attachment_item.contents     = text;
        attachment_item.download_url = url;
        attachment_item.fetching     = false;
    }

    const setLoadMethods = () => {
        LOAD_METHODS['image']   = loadImage;
        LOAD_METHODS['preview'] = loadPreview;
        LOAD_METHODS['video']   = loadVideo;
        LOAD_METHODS['audio']   = loadAudio;
        LOAD_METHODS['text']    = loadText;
        LOAD_METHODS['pdf']     = loadPdf;
    }

    const setActiveIndex = (index) => {
        active_index.value = index;
    }
</script>

<style scoped>
    .attachment-preview {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: 100%;
        max-width: 100vw;
        min-height: 300px;
        margin: 0 auto;
        height: calc(100vh - 120px); /* Ensure it fills the available vertical space */
        box-sizing: border-box;
    }

    .preview-video {
        width: 90vw;
        max-width: 90vw;
        height: calc(100vh - 120px);
        max-height: calc(100vh - 120px);
        display: block;
        margin: 0 auto;
        object-fit: contain;
    }
    .preview-pdf {
        width: 90vw;
        max-width: 90vw;
        height: calc(100vh - 120px);
        max-height: calc(100vh - 120px);
        border: none;
        display: block;
        margin: 0 auto;
    }
    .preview-text {
        width: 90vw;
        max-width: 90vw;
        max-height: calc(100vh - 120px);
        overflow: auto;
        background: #fff;
        border-radius: 4px;
        padding: 16px;
        margin: 0 auto;
        white-space: pre-wrap;
        word-break: break-all;
        justify-content: flex-start;
    }

    .preview-text pre {
        font-size: .8rem;
        width: 100%;
    }

    .preview-audio {
        width: 280px;
        min-width: 120px;
        max-width: 90vw;
        height: 100%;
        margin: auto;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    @media (max-width: 600px) {
        .preview-audio {
            width: 120px !important;
            min-width: 80px !important;
            max-width: 90vw !important;
        }
        .preview-image,
        .preview-video,
        .preview-pdf,
        .preview-text {
            max-width: 98vw !important;
        }
    }
</style>